import React, { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import { getAudit } from "../services/backend";

const load = (key: string) => {
  try {
    return JSON.parse(localStorage.getItem(key) || "[]");
  } catch {
    return [];
  }
};

interface EvidenceItem {
  label: string;
  count: number;
  detail: string;
}

export default function EvidencePack({ user }: { user?: any }) {
  const [audit, setAudit] = useState<any[]>([]);

  useEffect(() => {
    getAudit(500).then(a => setAudit(Array.isArray(a) ? a : []));
  }, []);

  const residents = load("residents_records");
  const invoices = load("invoices_records");
  const intelligence = load("intelligence_reports");
  const recon = load("recon_results");
  const deadlines = load("statutory_deadlines");
  const wounds = load("rmacs_wound_vault");
  const safeguarding = load("rmacs_safeguarding_vault");
  const policies = load("custom_policies");
  const reading = load("policy_reading_log");
  const feedback = load("feedback_records");

  const overdue = deadlines.filter((d: any) => {
    const dt = new Date(d.lastDate);
    dt.setMonth(dt.getMonth() + (parseInt(d.frequencyMonths, 10) || 12));
    return dt.getTime() < Date.now();
  });

  const sections: { question: string; items: EvidenceItem[] }[] = [
    {
      question: "Safe",
      items: [
        { label: "Safeguarding registry (Reg 13)", count: safeguarding.length, detail: "alerts recorded, managed and locked" },
        { label: "Wound care & tissue viability (Reg 12)", count: wounds.length, detail: "wound charts with staging and TVI referrals" },
        { label: "Statutory deadlines", count: overdue.length, detail: overdue.length === 0 ? "none overdue - compliant" : "OVERDUE - immediate action required" },
        { label: "Residents with funding/MCA context", count: residents.length, detail: "funding type and council recorded per resident" },
      ],
    },
    {
      question: "Effective",
      items: [
        { label: "AI admission intelligence reports", count: intelligence.length, detail: "assessment-driven care needs and DoLS narratives" },
        { label: "Dietary requirements generated", count: intelligence.reduce((s: number, i: any) => s + ((i.report && i.report.dietary) ? i.report.dietary.length : 0), 0), detail: "nutrition and hydration evidence" },
        { label: "Resident admission records", count: residents.length, detail: "complete admission documentation" },
      ],
    },
    {
      question: "Caring",
      items: [
        { label: "Next of kin / legal representative recorded", count: residents.filter((r: any) => r.familyName || r.legalRep).length, detail: "family partnership and rights evidence" },
        { label: "Feedback records", count: feedback.length, detail: "resident and family voice captured" },
      ],
    },
    {
      question: "Responsive",
      items: [
        { label: "Invoices and reconciliations", count: invoices.length + recon.length, detail: "transparent, funding-aware billing" },
        { label: "Personalised funding plans", count: residents.filter((r: any) => r.residentPays || r.councilPays).length, detail: "individual council / private shares recorded" },
      ],
    },
    {
      question: "Well-led",
      items: [
        { label: "Policies & SOPs", count: policies.length, detail: "controlled documents in force" },
        { label: "Policy reading attestations", count: reading.length, detail: "staff reading and declaration logs" },
        { label: "Tamper-evident audit trail entries", count: audit.length, detail: "immutable governance log" },
        { label: "Monthly financial reconciliations", count: recon.length, detail: "saved reconciliation reports" },
      ],
    },
  ];

  const exportPdf = () => {
    const body = sections.map(section => `
      <h3 style="margin:18px 0 6px;color:#123B36">${section.question}</h3>
      <table style="width:100%;border-collapse:collapse">
        <tr><th style="text-align:left;border-bottom:1px solid #ddd;padding:6px">Evidence</th><th style="border-bottom:1px solid #ddd;padding:6px">Records</th><th style="text-align:left;border-bottom:1px solid #ddd;padding:6px">Notes</th></tr>
        ${section.items.map(item => `<tr><td style="border-bottom:1px solid #eee;padding:6px">${item.label}</td><td style="border-bottom:1px solid #eee;padding:6px;text-align:center">${item.count}</td><td style="border-bottom:1px solid #eee;padding:6px">${item.detail}</td></tr>`).join("")}
      </table>`).join("");

    const html = `<!DOCTYPE html><html><head><title>Inspection Evidence Pack</title><style>body{font-family:Arial;margin:40px;color:#1f2937}h2{color:#123B36}</style></head><body>
<h2>CQC Inspection Evidence Pack - Roop Cottage Residential Care Home</h2>
<p>Generated ${new Date().toLocaleString()} by ${user && user.name ? user.name : "system"}. Records compiled automatically from live system data.</p>
${body}
<p style="margin-top:24px;font-size:12px">This pack is compiled from the tamper-evident RMACS audit and records system.</p>
</body></html>`;

    const win = window.open("", "_blank");
    if (win) { win.document.write(html); win.document.close(); win.focus(); win.print(); }
  };

  const exportExcel = () => {
    const workbook = XLSX.utils.book_new();
    sections.forEach(section => {
      const sheet = XLSX.utils.json_to_sheet(section.items.map(item => ({ Evidence: item.label, Records: item.count, Notes: item.detail })));
      XLSX.utils.book_append_sheet(workbook, sheet, section.question.slice(0, 31));
    });
    XLSX.writeFile(workbook, `evidence-pack-${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  return (
    <div className="space-y-6 p-6">
      <div>
        <h2 className="text-xl font-semibold text-slate-900">Inspection Evidence Pack</h2>
        <p className="text-sm text-slate-600">Live evidence compiled under the five CQC key questions. Export the full pack for the inspector in one click.</p>
      </div>

      <div className="flex flex-wrap gap-2">
        <button onClick={exportPdf} className="rounded-lg bg-red-700 px-4 py-2 text-sm font-medium text-white">Export pack as PDF</button>
        <button onClick={exportExcel} className="rounded-lg bg-green-700 px-4 py-2 text-sm font-medium text-white">Export pack as Excel</button>
      </div>

      {sections.map(section => (
        <div key={section.question} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <h3 className="mb-3 font-semibold text-[#123B36]">{section.question}</h3>
          <div className="space-y-2">
            {section.items.map(item => (
              <div key={item.label} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-100 p-2">
                <div className="text-sm text-slate-800">{item.label} <span className="text-xs text-slate-500">- {item.detail}</span></div>
                <div className={`rounded-full px-3 py-0.5 text-xs font-semibold ${item.label.includes("overdue") || item.label.includes("OVERDUE") ? (item.count > 0 ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700") : item.count > 0 ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>{item.count} records</div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}